import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BillingTransactionStatsFilters,
  BillingTransactionsFilters,
  BillingTransactionStatus,
} from "@/types/billing-transactions";
import { billingTransactionService } from "@/services/billing-transactions-service";

export const billingTransactionKeys = {
  all: ["billingTransactions"] as const,
  lists: () => [...billingTransactionKeys.all, "list"] as const,
  list: (filters?: BillingTransactionsFilters) =>
    [...billingTransactionKeys.lists(), filters] as const,
  stats: (filters?: BillingTransactionStatsFilters) =>
    [...billingTransactionKeys.all, "stats", filters] as const,
  details: () => [...billingTransactionKeys.all, "detail"] as const,
  detail: (id: string) => [...billingTransactionKeys.details(), id] as const,
};

export const useBillingTransactions = (
  page: number | null = 1,
  limit: number | null = 10,
  filters?: BillingTransactionsFilters,
) => {
  return useQuery({
    queryKey: billingTransactionKeys.list(filters),
    queryFn: () =>
      billingTransactionService.getBillingTransactions(
        filters,
        page || undefined,
        limit || undefined,
      ),
    staleTime: 5 * 60 * 1000,
  });
};

export const useBillingTransactionStats = (
  filters?: BillingTransactionStatsFilters,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: billingTransactionKeys.stats(filters),
    queryFn: () =>
      billingTransactionService.getBillingTransactionStats(filters),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

export const useUpdateTransactionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      newStatus,
    }: {
      id: string;
      newStatus: BillingTransactionStatus;
    }) => billingTransactionService.updateTransactionStatus(id, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: billingTransactionKeys.all,
      });
    },
  });
};

export const useBillingTransaction = (id: string) => {
  return useQuery({
    queryKey: billingTransactionKeys.detail(id),
    queryFn: () => billingTransactionService.getBillingTransactionById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};
